import { useState } from 'react'
import {
  Bot,
  Brain,
  Users,
  FileText,
  Palette,
  BarChart3,
  Cpu,
  ArrowRight,
  Zap,
  Shield,
  GitMerge,
  Layers,
  Target,
  Sparkles,
  CheckCircle2,
  ArrowDown
} from 'lucide-react'

export function ArchitectureShowcase() {
  const [activeLayer, setActiveLayer] = useState<string | null>(null)

  const layers = [
    {
      id: 'brand',
      name: 'Brand Intelligence Layer',
      icon: Layers,
      color: 'from-purple-500 to-indigo-600',
      description: 'Your brand DNA powers everything',
      features: ['Brand Voice', 'Visual Identity', 'Tone Guidelines', 'Competitor Intel']
    },
    {
      id: 'orchestration',
      name: '16 AI Agents + Orchestrator',
      icon: Bot,
      color: 'from-blue-500 to-cyan-600',
      description: 'Autonomous agents that coordinate intelligently',
      features: ['Content Writers', 'SEO Specialists', 'Ad Optimizers', 'Social Managers']
    },
    {
      id: 'workflow',
      name: 'Campaign Workflow Engine',
      icon: GitMerge,
      color: 'from-emerald-500 to-teal-600',
      description: 'From brief to execution in minutes',
      features: ['AI Briefs', 'Auto-Campaigns', 'Audience Targeting', 'Creative Generation']
    },
    {
      id: 'analytics',
      name: 'Analytics & Reporting',
      icon: BarChart3,
      color: 'from-orange-500 to-amber-600',
      description: 'Real-time insights across all channels',
      features: ['Attribution Models', 'ROI Tracking', 'Custom Reports', 'Dashboards']
    },
    {
      id: 'ml',
      name: 'ML Models & Predictions',
      icon: Brain,
      color: 'from-pink-500 to-rose-600',
      description: 'Predictive intelligence that improves over time',
      features: ['Performance Forecasts', 'Budget Optimization', 'Anomaly Detection', 'Recommendations']
    }
  ]

  return (
    <section className="py-24 bg-gradient-to-b from-gray-900 via-gray-900 to-black overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            The Complete Marketing OS
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Five integrated layers working together to automate your entire marketing operation
          </p>
        </div>

        {/* Main Architecture Visual */}
        <div className="relative">
          {/* Central Platform Container */}
          <div className="relative bg-gray-800/50  rounded-3xl border border-gray-700 p-8 md:p-12">

            {/* Stack Layers */}
            <div className="space-y-4">
              {layers.map((layer, index) => (
                <div
                  key={layer.id}
                  className={`relative group cursor-pointer transition-all duration-300 ${
                    activeLayer === layer.id ? 'scale-[1.02]' : 'hover:scale-[1.01]'
                  }`}
                  onMouseEnter={() => setActiveLayer(layer.id)}
                  onMouseLeave={() => setActiveLayer(null)}
                >
                  <div className={`
                    relative rounded-xl p-6
                    bg-gradient-to-r ${layer.color}
                    ${activeLayer === layer.id ? 'shadow-2xl shadow-current/30' : 'shadow-lg'}
                  `}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-white/[0.08] rounded-lg">
                          <layer.icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">{layer.name}</h3>
                          <p className="text-sm text-white/80">{layer.description}</p>
                        </div>
                      </div>

                      {/* Features Pills - desktop */}
                      <div className="hidden md:flex items-center gap-2">
                        {layer.features.map((feature) => (
                          <span
                            key={feature}
                            className="px-3 py-1 bg-white/[0.08] rounded-full text-xs text-white font-medium"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Features Pills - mobile horizontal scroll */}
                    <div className="flex md:hidden gap-2 mt-3 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
                      {layer.features.map((feature) => (
                        <span
                          key={feature}
                          className="px-3 py-1 bg-white/[0.08] rounded-full text-xs text-white font-medium whitespace-nowrap flex-shrink-0"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    {/* Connection Line */}
                    {index < layers.length - 1 && (
                      <div className="absolute left-1/2 -bottom-4 transform -translate-x-1/2 z-10">
                        <ArrowDown className="h-4 w-4 text-white/50" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Human in the Loop - Right Side */}
            <div className="absolute -right-4 md:right-8 top-1/2 transform translate-x-full -translate-y-1/2 hidden lg:block">
              <div className="relative">
                {/* Curved Arrow */}
                <svg className="absolute -left-24 top-1/2 -translate-y-1/2 w-20 h-32" viewBox="0 0 80 128">
                  <path
                    d="M0 64 Q40 64 40 0 M40 0 Q40 128 0 64"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-indigo-500"
                    strokeDasharray="4 4"
                  />
                  <polygon
                    points="0,60 8,64 0,68"
                    fill="currentColor"
                    className="text-indigo-500"
                  />
                </svg>

                {/* Human Icon */}
                <div className="bg-gray-800 border-2 border-indigo-500 rounded-2xl p-6 shadow-xl shadow-indigo-500/20">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-indigo-500/20 rounded-full flex items-center justify-center mb-3">
                      <Shield className="h-8 w-8 text-indigo-400" />
                    </div>
                    <h4 className="text-white font-semibold mb-1">Human in the Loop</h4>
                    <p className="text-xs text-gray-400 max-w-[120px]">
                      You approve what matters, AI handles the rest
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Orchestrator Badge - Top Right */}
            <div className="absolute -top-6 right-8 md:right-12">
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-full px-6 py-2 shadow-lg shadow-purple-500/30 flex items-center gap-2">
                <Cpu className="h-4 w-4 text-white" />
                <span className="text-sm font-semibold text-white">AI Orchestrator</span>
              </div>
            </div>
          </div>

          {/* Bottom Label */}
          <div className="text-center mt-8">
            <span className="text-2xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              Synap Marketing OS
            </span>
          </div>
        </div>

        {/* Workflow Benefits */}
        <div className="mt-20 grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
              <Zap className="h-6 w-6 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">10x Faster Execution</h3>
            <p className="text-gray-400 text-sm">
              From brief to live campaign in minutes, not weeks. Agents work in parallel.
            </p>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-xl mb-4">
              <Target className="h-6 w-6 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Perfect Coordination</h3>
            <p className="text-gray-400 text-sm">
              82% of conflicts auto-resolved. 6 detection types, 6 resolution strategies.
            </p>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
              <Sparkles className="h-6 w-6 text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Brand Consistency</h3>
            <p className="text-gray-400 text-sm">
              Every agent inherits your brand voice. Content stays on-brand automatically.
            </p>
          </div>
        </div>

        {/* Detailed Workflow Section */}
        <div className="mt-24">
          <h3 className="text-2xl font-bold text-white text-center mb-12">
            How It Works Together
          </h3>

          <div className="relative">
            {/* Workflow Steps */}
            <div className="grid md:grid-cols-5 gap-4">
              {[
                {
                  step: '1',
                  title: 'Define Brand',
                  desc: 'Voice, audience, competitors',
                  icon: Layers,
                  color: 'purple'
                },
                {
                  step: '2',
                  title: 'AI Brief',
                  desc: 'Strategy generated in seconds',
                  icon: FileText,
                  color: 'blue'
                },
                {
                  step: '3',
                  title: 'Agent Team',
                  desc: 'Auto-selected for your goals',
                  icon: Users,
                  color: 'emerald'
                },
                {
                  step: '4',
                  title: 'Execute',
                  desc: 'Content, ads, emails created',
                  icon: Palette,
                  color: 'orange'
                },
                {
                  step: '5',
                  title: 'Optimize',
                  desc: 'ML improves performance',
                  icon: Brain,
                  color: 'pink'
                }
              ].map((item, index) => (
                <div key={item.step} className="relative">
                  <div className={`
                    bg-gray-800/80 border border-gray-700 rounded-xl p-5
                    hover:border-${item.color}-500/50 transition-all
                  `}>
                    <div className={`
                      w-8 h-8 rounded-full bg-${item.color}-500/20
                      flex items-center justify-center mb-3
                    `}>
                      <span className={`text-sm font-bold text-${item.color}-400`}>
                        {item.step}
                      </span>
                    </div>
                    <item.icon className={`h-5 w-5 text-${item.color}-400 mb-2`} />
                    <h4 className="text-white font-semibold text-sm mb-1">{item.title}</h4>
                    <p className="text-white/50 text-xs">{item.desc}</p>
                  </div>

                  {/* Arrow */}
                  {index < 4 && (
                    <div className="hidden md:block absolute top-1/2 -right-2 transform translate-x-1/2 -translate-y-1/2 z-10">
                      <ArrowRight className="h-4 w-4 text-white/60" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <a
            href="/pricing"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-500 hover:to-purple-500 transition-all shadow-lg shadow-indigo-500/30"
          >
            Start Free Trial
            <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </div>
    </section>
  )
}

export default ArchitectureShowcase
